import React, { useMemo, useState } from 'react';
import { format, startOfWeek } from 'date-fns';
import { PageHeader } from '../components/PageHeader';
import { LiveOrderFeed } from '../components/LiveOrderFeed';
import { useLiveOrders } from '../hooks/useLiveOrders';
import { orderCommodities } from '../data/orders';
import { company } from '../data/company';
import { quickOrderMailto } from '../utils/contact';
import { TradeOrder } from '../types';
const statusColor: Record<TradeOrder['status'], string> = {
  Confirmed: 'text-gold-light',
  'Assay pending': 'text-amber-200',
  'In transit': 'text-sky-200',
  'Cleared customs': 'text-violet-200',
  Delivered: 'text-emerald-200'
};
export function LiveOrders() {
  const {
    orders,
    latestId
  } = useLiveOrders({
    weeks: 9,
    perWeek: 6
  });
  const [filter, setFilter] = useState<string>('all');
  const filtered = useMemo(() => filter === 'all' ? orders : orders.filter(o => o.commodity === filter), [orders, filter]);
  const weeks = useMemo(() => {
    const groups = new Map<number, TradeOrder[]>();
    filtered.forEach(order => {
      const key = startOfWeek(order.timestamp, {
        weekStartsOn: 1
      }).getTime();
      const list = groups.get(key);
      if (list) list.push(order);else groups.set(key, [order]);
    });
    return Array.from(groups.entries()).sort((a, b) => b[0] - a[0]).slice(0, 9);
  }, [filtered]);
  return <>
      <PageHeader eyebrow="Order book" title="Every order, logged as it lands" intro="A rolling nine-week record of confirmed purchase orders — commodity, quantity, destination, incoterm, date and time. New orders appear at the top of the feed the moment the desk countersigns." image={company.images.logistics} imageAlt="Export container being sealed at the dry port" data-id="element-419" data-mp="1n81h2ww3dmkv" />

      <section className="mx-auto max-w-shell px-6 py-16" data-id="element-420" data-mp="2615b4jd821g0">
        <div className="grid gap-10 lg:grid-cols-[1fr_360px] lg:items-start" data-id="element-421" data-mp="218c7jythoz5i">
          <div data-id="element-422" data-mp="3vgod8erovzk0">
            <div className="flex flex-wrap items-center gap-2" data-id="element-423" data-mp="3vg6bx3q66v58">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted" data-id="element-424" data-mp="3vj28ooqa7ds3">
                Filter
              </span>
              {['all', ...orderCommodities.map(c => c.name)].map(value => {
              const selected = filter === value;
              return <button key={value} type="button" onClick={() => setFilter(value)} className={`rounded-sm border px-3 py-1.5 text-xs transition-colors duration-150 ${selected ? 'border-gold bg-gold/10 text-gold-light' : 'border-hair text-muted hover:border-gold/50 hover:text-bone'}`} data-id="element-425" data-mp="3qnvdk5b2vhes">
                    {value === 'all' ? 'All commodities' : value}
                  </button>;
            })}
            </div>

            <div className="mt-8 space-y-10" data-id="element-426" data-mp="1g3fm3oun0693">
              {weeks.map(([weekStart, weekOrders]) => <div key={weekStart} data-id="element-427" data-mp="1b4uhld8cr0eu">
                  <div className="flex items-baseline justify-between border-b border-hair pb-3" data-id="element-428" data-mp="1b5vzo1nscn62">
                    <h2 className="font-display text-2xl text-bone" data-id="element-429" data-mp="1owskv7fdu233">
                      Week of {format(new Date(weekStart), 'dd MMMM yyyy')}
                    </h2>
                    <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted" data-id="element-430" data-mp="1ytdte2qm1vbo">
                      {weekOrders.length} orders
                    </p>
                  </div>

                  <div className="overflow-x-auto thin-scroll" data-id="element-431" data-mp="27vd9mstswvdk">
                    <table className="w-full min-w-[720px] border-collapse text-left" data-id="element-432" data-mp="27sirj7y9fmnb">
                      <caption className="sr-only" data-id="element-433" data-mp="23r5k4tfirbea">
                        Orders confirmed during the week of{' '}
                        {format(new Date(weekStart), 'dd MMMM yyyy')}
                      </caption>
                      <thead data-id="element-434" data-mp="awedyx7fcyac">
                        <tr className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted" data-id="element-435" data-mp="ax7cxc056jhx">
                          <th scope="col" className="py-3 pr-4 font-medium" data-id="element-436" data-mp="fupn15pwb4ri">Reference</th>
                          <th scope="col" className="py-3 pr-4 font-medium" data-id="element-437" data-mp="jwkqafrdzs7x">Commodity</th>
                          <th scope="col" className="py-3 pr-4 font-medium" data-id="element-438" data-mp="ovxccyafov9g">Quantity</th>
                          <th scope="col" className="py-3 pr-4 font-medium" data-id="element-439" data-mp="3obalvtotchgw">Destination</th>
                          <th scope="col" className="py-3 pr-4 font-medium" data-id="element-440" data-mp="3scvwt9hselog">Terms</th>
                          <th scope="col" className="py-3 pr-4 font-medium" data-id="element-441" data-mp="3sat6eocq2qj7">Date &amp; time</th>
                          <th scope="col" className="py-3 font-medium" data-id="element-442" data-mp="14i4kpzw1lqt">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-hair/70" data-id="element-443" data-mp="34nuy4dyraowd">
                        {weekOrders.map(order => <tr key={order.id} className="text-sm" data-id="element-444" data-mp="39lcyhfypjvsr">
                            <td className="py-3.5 pr-4 font-mono text-xs text-muted" data-id="element-445" data-mp="322n3wukk25di">{order.id}</td>
                            <td className="py-3.5 pr-4 text-bone" data-id="element-446" data-mp="2x4258zkybtr6">{order.commodity}</td>
                            <td className="py-3.5 pr-4 font-mono text-gold-light" data-id="element-447" data-mp="2s4pguaefmemh">{order.quantity}</td>
                            <td className="py-3.5 pr-4 text-bone/80" data-id="element-448" data-mp="2o3e0ywld17rh">{order.destination}</td>
                            <td className="py-3.5 pr-4 font-mono text-xs text-muted" data-id="element-449" data-mp="2j414dk4osmbp">
                              {order.incoterm}
                            </td>
                            <td className="py-3.5 pr-4 font-mono text-xs text-muted" data-id="element-450" data-mp="2ebzug4uzr7ou">
                              {format(order.timestamp, 'dd MMM yyyy · HH:mm')}
                            </td>
                            <td className={`py-3.5 text-xs ${statusColor[order.status]}`} data-id="element-451" data-mp="24gy63b8ji9nb">
                              {order.status}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>)}
            </div>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-28" data-id="element-452" data-mp="1pth9tv8yfsp2">
            <LiveOrderFeed orders={orders} latestId={latestId} limit={5} data-id="element-453" data-mp="1bsn3di5gw1t1" />
            <div className="border border-hair bg-panel p-6" data-id="element-454" data-mp="1burfuohfm94x">
              <h2 className="font-display text-xl text-bone" data-id="element-455" data-mp="1gssvt0jk9sc6">Add your order to the book</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-456" data-mp="24idh3q36t1om">
                Send commodity, quantity, destination port and incoterm. The desk responds with a firm
                offer and draft contract within 24 hours.
              </p>
              <a href={quickOrderMailto()} className="mt-5 block rounded-sm bg-gold px-5 py-3 text-center text-sm font-semibold text-ink transition-colors duration-150 hover:bg-gold-light" data-id="element-457" data-mp="1upok208dvqct">
                Place an Order
              </a>
            </div>
            <p className="text-xs leading-relaxed text-muted/80" data-id="element-458" data-mp="2s8pzjuw26gtg">
              Buyer identities are abbreviated for confidentiality. Reference numbers can be verified
              with the trading desk on request.
            </p>
          </aside>
        </div>
      </section>
    </>;
}